import { FC, MouseEvent } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import styled, { css } from "styled-components";

import FileField from "@/components/Forms/FileField";
import Button from "@/components/Button";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import { Actions } from "@/components/AlertDialogs/Dialog";
import { ImportType } from "./index";

const Form = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StyledActions = styled(Actions)`
  margin-top: auto;
`;

const StyledFileField = styled(FileField)<{ $isFullHeight: boolean }>`
  ${({ $isFullHeight }) =>
    $isFullHeight &&
    css`
      height: 100%;
      > div {
        height: 100%;
      }
    `}
`;

const defaultValues = {
  csv: null,
};

type TFormData = {
  csv: File | null;
};

interface Props {
  onClose: () => void;
  activeImportType: ImportType;
}

const UploadForm: FC<Props> = ({ onClose, activeImportType }) => {
  const alerts = useAlerts();

  const methods = useForm<TFormData>({
    defaultValues,
  });

  const file = useWatch({ control: methods.control, name: "csv" });

  const onSubmit = methods.handleSubmit(async ({ csv }) => {
    const formData = new FormData();

    if (csv) {
      formData.append("csv", csv);
    }

    if (activeImportType.postBody) {
      Object.keys(activeImportType.postBody).forEach((key) => {
        formData.append(key, activeImportType.postBody![key]);
      });
    }

    try {
      await api.post(activeImportType.url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      onClose();
      methods.reset(defaultValues);
      alerts.success("Import successfully started");
    } catch (error) {
      handleFormErrors<TFormData>(error, methods.setError);
    }
  });

  const onCloseWithEventPrevention = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const { isValid, isSubmitting } = methods.formState;

  return (
    <Form onSubmit={onSubmit}>
      <FormProvider {...methods}>
        <StyledFileField
          $isFullHeight={!!!file}
          fileType="other"
          name="csv"
          notes="CSV"
          accept=".csv"
        />
      </FormProvider>
      {!!file && (
        <StyledActions>
          <Button loading={isSubmitting} disabled={isSubmitting || !isValid}>
            Import
          </Button>
          <Button onClick={onCloseWithEventPrevention} color="secondary">
            Cancel
          </Button>
        </StyledActions>
      )}
    </Form>
  );
};

export default UploadForm;
